import type { GameState } from '@/types';
import logger from '@/utils/logger';

/**
 * Utility functions for managing student URL parameters and navigation
 */

export interface StudentUrlParams {
  sessionId: string | null;
  playerId: string | null;
  gameCode: string | null;
}

/**
 * Extract and validate student URL parameters
 */
export const getStudentUrlParams = (): StudentUrlParams => {
  const urlParams = new URLSearchParams(window.location.search);

  const sessionId = urlParams.get('sessionId');
  const playerId = urlParams.get('playerId');

  // Extract game code from path if on join-game route
  let gameCode: string | null = null;
  const joinGameMatch = window.location.pathname.match(/\/join-game\/([A-Z0-9]+)/);
  if (joinGameMatch) {
    gameCode = joinGameMatch[1];
  }

  return { sessionId, playerId, gameCode };
};

/**
 * Validate that required URL parameters are present for student routes
 */
export const validateStudentUrlParams = (params: StudentUrlParams): {
  isValid: boolean;
  missingParams: string[];
  recommendations: string[];
} => {
  const missingParams: string[] = [];
  const recommendations: string[] = [];

  if (!params.sessionId) {
    missingParams.push('sessionId');
    recommendations.push('Ensure you joined the game properly or refresh the page');
  }

  // Player ID is not always required (e.g., before joining)
  const currentPath = window.location.pathname;
  const isInGame = ['/question', '/game', '/scores', '/bonus', '/final-scores'].some(path =>
    currentPath.startsWith(path)
  );

  if (isInGame && !params.playerId) {
    missingParams.push('playerId');
    recommendations.push('You may need to rejoin the game session');
  }

  return {
    isValid: missingParams.length === 0,
    missingParams,
    recommendations
  };
};

/**
 * Ensure URL has proper student parameters for auto-navigation
 */
export const ensureStudentUrlParams = (sessionId: string, playerId?: string): void => {
  const currentParams = new URLSearchParams(window.location.search);
  let hasChanges = false;

  // Ensure sessionId is present
  if (!currentParams.has('sessionId') || currentParams.get('sessionId') !== sessionId) {
    currentParams.set('sessionId', sessionId);
    hasChanges = true;
  }

  // Add playerId if provided and not present
  if (playerId && (!currentParams.has('playerId') || currentParams.get('playerId') !== playerId)) {
    currentParams.set('playerId', playerId);
    hasChanges = true;
  }

  if (hasChanges) {
    const newUrl = `${window.location.pathname}?${currentParams.toString()}`;
    logger.debug('URL parameters updated', {
      oldUrl: window.location.href,
      newUrl,
      sessionId,
      playerId
    });

    // Use replaceState to avoid adding to browser history
    window.history.replaceState({}, '', newUrl);
  }
};

/**
 * Create a student URL with proper parameters for navigation
 */
export const createStudentUrl = (path: string, sessionId: string, playerId?: string): string => {
  const params = new URLSearchParams();
  params.set('sessionId', sessionId);

  if (playerId) {
    params.set('playerId', playerId);
  }

  return `${path}?${params.toString()}`;
};

/**
 * Resolve the canonical student route for a given live session state.
 */
export const getStudentRouteForGameState = (gameState: GameState | null | undefined): string | null => {
  switch (gameState) {
    case 'lobby':
      return '/lobby';
    case 'countdown':
      return '/countdown';
    case 'question':
      return '/question';
    case 'running':
      return '/game';
    case 'scoring':
    case 'leaderboard':
      return '/scores';
    case 'bonus':
      return '/bonus';
    case 'finished':
      return '/final-scores';
    default:
      return null;
  }
};

/**
 * Check if current route is a student route that needs monitoring
 */
export const isStudentRoute = (pathname: string = window.location.pathname): boolean => {
  const studentRoutes = [
    '/join-game',
    '/lobby',
    '/countdown',
    '/question',
    '/game',
    '/scores',
    '/bonus',
    '/final-scores'
  ];
  const normalizedPath = pathname !== '/' && pathname.endsWith('/')
    ? pathname.slice(0, -1)
    : pathname;

  return studentRoutes.some(route =>
    normalizedPath === route || normalizedPath.startsWith(`${route}/`)
  );
};

/**
 * Debug helper to log current URL state
 */
export const debugUrlState = (context: string = 'Unknown'): void => {
  const params = getStudentUrlParams();
  const validation = validateStudentUrlParams(params);

  logger.debug(`URL Debug [${context}]`, {
    url: window.location.href,
    pathname: window.location.pathname,
    search: window.location.search,
    params,
    validation,
    isStudentRoute: isStudentRoute(),
    timestamp: new Date().toISOString()
  });
};
